import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import {
  ArrowLeft, Save, Loader2, Trash2, Plus, ChevronDown, MapPin, Eye,
} from "lucide-react";

const emptyEvent = {
  title: "", slug: "", slogan: "", short_description: "", description: "",
  start_at: "", end_at: "", all_day: false, timezone: "Europe/Rome",
  location_name: "", address: "", city: "Sanluri", province: "SU", postal_code: "09025", country: "IT",
  latitude: null, longitude: null,
  is_free: true, price_from: null, price_currency: "EUR", price_note: "", ticket_url: "",
  registration_required: false, registration_url: "", capacity: null, min_age: null, max_age: null,
  cover_image: "", gallery: [],
  category_slug: "", category_label: "",
  badges: [], tags: [], timeline: [], faqs: [], downloads: [], amenities: [],
  event_website: "", event_facebook: "", event_instagram: "", event_youtube: "", event_hashtag: "",
  organizers: [{ name: "", role: "organizer", phone: "", email: "", website: "" }],
  status: "draft", priority: "normal", featured: false, display_order: 100,
  meta_title: "", meta_description: "", noindex: false, sitemap_priority: 0.5,
  custom_fields: {}, internal_notes: "", last_change_reason: "",
};

const CATEGORIES = [
  { slug: "gastronomia", label: "Gastronomia" },
  { slug: "musica", label: "Musica" },
  { slug: "cultura", label: "Cultura" },
  { slug: "sport", label: "Sport" },
  { slug: "mercato", label: "Mercato" },
  { slug: "famiglia", label: "Famiglia" },
  { slug: "religione", label: "Religione / Tradizioni" },
  { slug: "arte", label: "Arte / Mostre" },
  { slug: "cinema", label: "Cinema / Teatro" },
  { slug: "workshop", label: "Workshop / Formazione" },
  { slug: "beneficenza", label: "Beneficenza" },
  { slug: "altro", label: "Altro" },
];

const BADGE_OPTIONS = ["free","music","family","pet_friendly","accessible","street_food","culture","booking_required","outdoor","indoor","livestream"];
const BADGE_LABELS = {
  free: "Gratuito", music: "Musica dal vivo", family: "Per famiglie",
  pet_friendly: "Pet friendly", accessible: "Accessibile", street_food: "Street food",
  culture: "Cultura", booking_required: "Su prenotazione", outdoor: "All'aperto",
  indoor: "Al chiuso", livestream: "In streaming",
};
const AMENITY_OPTIONS = ["parking","accessible","kids_area","pets_allowed","food_point","restrooms","wifi","shelter","first_aid","info_point","coat_check","stroller_friendly"];
const AMENITY_LABELS = {
  parking: "Parcheggio", accessible: "Accessibile", kids_area: "Area bambini",
  pets_allowed: "Animali ammessi", food_point: "Punto ristoro", restrooms: "Servizi igienici",
  wifi: "Wi-Fi", shelter: "Riparato pioggia", first_aid: "Primo soccorso",
  info_point: "Info point", coat_check: "Guardaroba", stroller_friendly: "Passeggini",
};

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const Section = ({ title, kicker, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-6 py-5 hover:bg-slate-50 transition-colors" data-testid={testId}>
        <div className="text-left">
          {kicker && <div className="text-[10px] uppercase tracking-[0.22em] text-[#12964A] font-semibold">{kicker}</div>}
          <div className="font-display font-bold text-lg text-stone-900">{title}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-6 pt-2 border-t border-slate-100">{children}</div>}
    </div>
  );
};

const Field = ({ label, hint, children, required = false }) => (
  <label className="block">
    <div className="text-xs font-semibold text-stone-700 mb-1.5">{label}{required && <span className="text-red-500">*</span>}</div>
    {children}
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </label>
);

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#12964A] focus:ring-2 focus:ring-[#12964A]/15 transition-all";

const ChipToggle = ({ options, labels, value = [], onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button
          key={o}
          type="button"
          onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`text-xs px-3.5 py-2 rounded-full border transition-all ${on ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#12964A]"}`}
        >
          {labels?.[o] || o}
        </button>
      );
    })}
  </div>
);

const dtLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};

const EventForm = () => {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [ev, setEv] = useState(emptyEvent);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagsInput, setTagsInput] = useState("");

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminApi.getEvent(id)
      .then((r) => {
        const merged = { ...emptyEvent, ...r.data };
        setEv(merged);
        setTagsInput((merged.tags || []).join(", "));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Impossibile caricare l'evento"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k, v) => setEv((p) => ({ ...p, [k]: v }));

  const onTitleChange = (v) => {
    set("title", v);
    if (isNew && !ev.slug) set("slug", slugify(v));
  };

  const addTimeline = () => set("timeline", [...(ev.timeline || []), { time: "", title: "", description: "" }]);
  const updTimeline = (i, k, v) => set("timeline", ev.timeline.map((t, j) => j === i ? { ...t, [k]: v } : t));
  const delTimeline = (i) => set("timeline", ev.timeline.filter((_, j) => j !== i));

  const addFaq = () => set("faqs", [...(ev.faqs || []), { question: "", answer: "" }]);
  const updFaq = (i, k, v) => set("faqs", ev.faqs.map((f, j) => j === i ? { ...f, [k]: v } : f));
  const delFaq = (i) => set("faqs", ev.faqs.filter((_, j) => j !== i));

  const addGallery = () => set("gallery", [...(ev.gallery || []), { type: "image", url: "", alt: "" }]);
  const updGallery = (i, k, v) => set("gallery", ev.gallery.map((g, j) => j === i ? { ...g, [k]: v } : g));
  const delGallery = (i) => set("gallery", ev.gallery.filter((_, j) => j !== i));

  const addDownload = () => set("downloads", [...(ev.downloads || []), { title: "", url: "", mime_type: "application/pdf", file_size_label: "" }]);
  const updDownload = (i, k, v) => set("downloads", ev.downloads.map((d, j) => j === i ? { ...d, [k]: v } : d));
  const delDownload = (i) => set("downloads", ev.downloads.filter((_, j) => j !== i));

  const updOrg = (i, k, v) => set("organizers", ev.organizers.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const addOrg = () => set("organizers", [...(ev.organizers || []), { name: "", role: "co-organizer", phone: "", email: "", website: "" }]);
  const delOrg = (i) => set("organizers", ev.organizers.filter((_, j) => j !== i));

  const handleSave = async (nextStatus) => {
    setSaving(true); setError(null);
    try {
      // Normalize
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        ...ev,
        tags,
        category_label: ev.category_slug ? (CATEGORIES.find((c) => c.slug === ev.category_slug)?.label || ev.category_slug) : null,
      };
      if (nextStatus) payload.status = nextStatus;
      // ISO datetime normalization
      if (payload.start_at && payload.start_at.length === 16) payload.start_at = new Date(payload.start_at).toISOString();
      if (payload.end_at && payload.end_at.length === 16) payload.end_at = new Date(payload.end_at).toISOString();
      // Remove empty organizers
      payload.organizers = (payload.organizers || []).filter((o) => (o.name || "").trim());
      if (!payload.title || !payload.start_at) {
        setError("Titolo e data di inizio sono obbligatori");
        setSaving(false);
        return;
      }

      const res = isNew ? await adminApi.createEvent(payload) : await adminApi.updateEvent(id, payload);
      if (isNew) nav(`/admin/eventi/${res.data.id}`, { replace: true });
      else setEv({ ...emptyEvent, ...res.data });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : "Errore durante il salvataggio"));
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { /* noop */ }
    } finally {
      setSaving(false);
    }
  };

  const geocode = async () => {
    if (!ev.address) return;
    try {
      const q = encodeURIComponent(`${ev.address}, ${ev.city || "Sanluri"}, Italia`);
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`);
      const j = await r.json();
      if (j[0]) {
        set("latitude", parseFloat(j[0].lat));
        set("longitude", parseFloat(j[0].lon));
      }
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="p-16 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-8">
      <Link to="/admin/eventi" className="inline-flex items-center gap-2 text-slate-500 hover:text-[#12964A] mb-6"><ArrowLeft className="w-4 h-4" /> Torna agli eventi</Link>

      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">{isNew ? "Nuovo evento" : "Modifica evento"}</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">{ev.title || "Senza titolo"}</h1>
          {!isNew && ev.slug && <p className="text-slate-500 mt-2 text-sm">Slug: /eventi/{ev.slug}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && ev.status === "published" && ev.slug && (
            <a href={`/eventi/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="preview-event"><Eye className="w-4 h-4" /> Anteprima pubblica</a>
          )}
          <button onClick={() => handleSave("draft")} disabled={saving} className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-5 py-2.5 text-sm bg-white hover:border-slate-400 disabled:opacity-60" data-testid="btn-save-draft">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva bozza
          </button>
          <button onClick={() => handleSave("published")} disabled={saving} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm transition-colors disabled:opacity-60" data-testid="btn-publish">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Pubblica
          </button>
        </div>
      </div>

      {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

      <div className="space-y-4">
        {/* 1. Informazioni base */}
        <Section title="Informazioni base" kicker="Sezione 1" testId="sec-base">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Titolo" required>
              <input className={inputCls} value={ev.title} onChange={(e) => onTitleChange(e.target.value)} data-testid="input-title" placeholder="Es. Sagra della Cassola 2026" />
            </Field>
            <Field label="Slug (URL)" hint="Solo minuscole, trattini invece degli spazi">
              <input className={inputCls} value={ev.slug} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="input-slug" />
            </Field>
            <Field label="Slogan / Sottotitolo">
              <input className={inputCls} value={ev.slogan || ""} onChange={(e) => set("slogan", e.target.value)} />
            </Field>
            <Field label="Categoria">
              <select className={inputCls} value={ev.category_slug || ""} onChange={(e) => set("category_slug", e.target.value)} data-testid="input-category">
                <option value="">Seleziona…</option>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Descrizione breve" hint="Occhiello 1-2 righe (usato in hero e liste)">
              <textarea className={inputCls} rows={2} value={ev.short_description || ""} onChange={(e) => set("short_description", e.target.value)} data-testid="input-short-desc" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Descrizione completa" hint="Testo lungo — supporta a-capo semplici">
              <textarea className={inputCls} rows={7} value={ev.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="input-desc" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Field label="Edizione" hint="Es. XV Edizione">
              <input className={inputCls} value={ev.edition || ""} onChange={(e) => set("edition", e.target.value)} />
            </Field>
            <Field label="Hashtag ufficiale">
              <input className={inputCls} value={ev.event_hashtag || ""} onChange={(e) => set("event_hashtag", e.target.value)} placeholder="#SagraCassola2026" />
            </Field>
          </div>
        </Section>

        {/* 2. Data e orario */}
        <Section title="Data e orario" kicker="Sezione 2" testId="sec-date">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Inizio" required>
              <input type="datetime-local" className={inputCls} value={dtLocal(ev.start_at)} onChange={(e) => set("start_at", e.target.value)} data-testid="input-start" />
            </Field>
            <Field label="Fine">
              <input type="datetime-local" className={inputCls} value={dtLocal(ev.end_at)} onChange={(e) => set("end_at", e.target.value)} data-testid="input-end" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!ev.all_day} onChange={(e) => set("all_day", e.target.checked)} className="w-4 h-4" /> Evento a giornata intera (nasconde orario)
            </label>
            <Field label="Nota durata (opz.)">
              <input className={inputCls} value={ev.duration_note || ""} onChange={(e) => set("duration_note", e.target.value)} placeholder="Circa 3 ore" />
            </Field>
          </div>
        </Section>

        {/* 3. Luogo */}
        <Section title="Luogo" kicker="Sezione 3" testId="sec-place">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome luogo" hint="Es. Piazza Castello">
              <input className={inputCls} value={ev.location_name || ""} onChange={(e) => set("location_name", e.target.value)} />
            </Field>
            <Field label="Indirizzo completo">
              <input className={inputCls} value={ev.address || ""} onChange={(e) => set("address", e.target.value)} onBlur={geocode} data-testid="input-address" />
            </Field>
            <Field label="Città"><input className={inputCls} value={ev.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="CAP"><input className={inputCls} value={ev.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} /></Field>
            <Field label="Latitudine"><input className={inputCls} type="number" step="0.0000001" value={ev.latitude ?? ""} onChange={(e) => set("latitude", e.target.value ? parseFloat(e.target.value) : null)} /></Field>
            <Field label="Longitudine"><input className={inputCls} type="number" step="0.0000001" value={ev.longitude ?? ""} onChange={(e) => set("longitude", e.target.value ? parseFloat(e.target.value) : null)} /></Field>
          </div>
          <button type="button" onClick={geocode} className="mt-3 inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline"><MapPin className="w-4 h-4" /> Geocodifica automatica</button>
          <div className="mt-4">
            <Field label="Note luogo (accessi, indicazioni)"><input className={inputCls} value={ev.venue_notes || ""} onChange={(e) => set("venue_notes", e.target.value)} /></Field>
          </div>
        </Section>

        {/* 4. Organizzatori */}
        <Section title="Organizzatori" kicker="Sezione 4" testId="sec-org">
          {ev.organizers.map((o, i) => (
            <div key={i} className="border border-slate-200 rounded-2xl p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-stone-700">Organizzatore #{i + 1}</div>
                {ev.organizers.length > 1 && <button type="button" onClick={() => delOrg(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Nome"><input className={inputCls} value={o.name} onChange={(e) => updOrg(i, "name", e.target.value)} /></Field>
                <Field label="Ruolo">
                  <select className={inputCls} value={o.role} onChange={(e) => updOrg(i, "role", e.target.value)}>
                    <option value="organizer">Organizzatore</option><option value="co-organizer">Co-organizzatore</option>
                    <option value="sponsor">Sponsor</option><option value="patron">Patrocinatore</option>
                    <option value="host">Host</option><option value="partner">Partner</option>
                  </select>
                </Field>
                <Field label="Telefono"><input className={inputCls} value={o.phone || ""} onChange={(e) => updOrg(i, "phone", e.target.value)} /></Field>
                <Field label="Email"><input className={inputCls} type="email" value={o.email || ""} onChange={(e) => updOrg(i, "email", e.target.value)} /></Field>
                <Field label="Sito web"><input className={inputCls} value={o.website || ""} onChange={(e) => updOrg(i, "website", e.target.value)} /></Field>
                <Field label="Nota (patrocini)"><input className={inputCls} value={o.note || ""} onChange={(e) => updOrg(i, "note", e.target.value)} /></Field>
                <Field label="Facebook"><input className={inputCls} value={o.facebook || ""} onChange={(e) => updOrg(i, "facebook", e.target.value)} /></Field>
                <Field label="Instagram"><input className={inputCls} value={o.instagram || ""} onChange={(e) => updOrg(i, "instagram", e.target.value)} /></Field>
              </div>
            </div>
          ))}
          <button type="button" onClick={addOrg} className="inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline"><Plus className="w-4 h-4" /> Aggiungi organizzatore</button>
        </Section>

        {/* 5. Costo */}
        <Section title="Costo & prenotazione" kicker="Sezione 5" testId="sec-price">
          <label className="inline-flex items-center gap-2 mb-4">
            <input type="checkbox" checked={!!ev.is_free} onChange={(e) => set("is_free", e.target.checked)} className="w-4 h-4" /> Ingresso libero
          </label>
          {!ev.is_free && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Da (€)"><input type="number" step="0.01" className={inputCls} value={ev.price_from ?? ""} onChange={(e) => set("price_from", e.target.value ? parseFloat(e.target.value) : null)} /></Field>
              <Field label="A (€)"><input type="number" step="0.01" className={inputCls} value={ev.price_to ?? ""} onChange={(e) => set("price_to", e.target.value ? parseFloat(e.target.value) : null)} /></Field>
              <Field label="Nota costo"><input className={inputCls} value={ev.price_note || ""} onChange={(e) => set("price_note", e.target.value)} /></Field>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <Field label="URL biglietteria (Eventbrite ecc.)"><input className={inputCls} value={ev.ticket_url || ""} onChange={(e) => set("ticket_url", e.target.value)} /></Field>
            <Field label="URL prenotazione"><input className={inputCls} value={ev.registration_url || ""} onChange={(e) => set("registration_url", e.target.value)} /></Field>
            <Field label="Capienza"><input type="number" className={inputCls} value={ev.capacity ?? ""} onChange={(e) => set("capacity", e.target.value ? parseInt(e.target.value) : null)} /></Field>
            <Field label="Età minima"><input type="number" className={inputCls} value={ev.min_age ?? ""} onChange={(e) => set("min_age", e.target.value ? parseInt(e.target.value) : null)} /></Field>
          </div>
        </Section>

        {/* 6. Media */}
        <Section title="Media (cover + gallery)" kicker="Sezione 6" testId="sec-media">
          <Field label="Cover image URL" hint="16:9 consigliato (min 1600×900)"><input className={inputCls} value={ev.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} data-testid="input-cover" /></Field>
          {ev.cover_image && <img src={ev.cover_image} alt="" className="mt-3 rounded-xl w-full max-w-md aspect-[16/9] object-cover" />}
          <div className="mt-6">
            <div className="text-xs font-semibold text-stone-700 mb-2">Gallery ({(ev.gallery || []).length})</div>
            {ev.gallery.map((g, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <select className={inputCls + " w-28"} value={g.type || "image"} onChange={(e) => updGallery(i, "type", e.target.value)}>
                  <option value="image">Immagine</option><option value="video">Video MP4</option>
                  <option value="youtube">YouTube</option><option value="vimeo">Vimeo</option>
                </select>
                <input className={inputCls + " flex-1"} placeholder="URL" value={g.url || ""} onChange={(e) => updGallery(i, "url", e.target.value)} />
                <input className={inputCls + " w-40"} placeholder="Alt text" value={g.alt || ""} onChange={(e) => updGallery(i, "alt", e.target.value)} />
                <button type="button" onClick={() => delGallery(i)} className="text-red-500 hover:text-red-700 mt-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addGallery} className="inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline mt-2"><Plus className="w-4 h-4" /> Aggiungi media</button>
          </div>
        </Section>

        {/* 7. Badge & Tag */}
        <Section title="Badge & Tag" kicker="Sezione 7" defaultOpen={false} testId="sec-badges">
          <div className="text-xs font-semibold text-stone-700 mb-2">Badge</div>
          <ChipToggle options={BADGE_OPTIONS} labels={BADGE_LABELS} value={ev.badges || []} onChange={(v) => set("badges", v)} />
          <div className="mt-4">
            <Field label="Tag" hint="Separati da virgola, es: sagra, gastronomia, folkloresardo">
              <input className={inputCls} value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* 8. Programma (timeline) */}
        <Section title="Programma / Timeline" kicker="Sezione 8" defaultOpen={false} testId="sec-timeline">
          {ev.timeline.map((t, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[100px_1fr_2fr_auto] gap-2 items-start mb-2">
              <input className={inputCls} placeholder="18:00" value={t.time || ""} onChange={(e) => updTimeline(i, "time", e.target.value)} />
              <input className={inputCls} placeholder="Titolo" value={t.title || ""} onChange={(e) => updTimeline(i, "title", e.target.value)} />
              <input className={inputCls} placeholder="Descrizione (opzionale)" value={t.description || ""} onChange={(e) => updTimeline(i, "description", e.target.value)} />
              <button type="button" onClick={() => delTimeline(i)} className="text-red-500 hover:text-red-700 mt-2.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addTimeline} className="inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline mt-2"><Plus className="w-4 h-4" /> Aggiungi tappa</button>
        </Section>

        {/* 9. FAQ */}
        <Section title="FAQ" kicker="Sezione 9" defaultOpen={false} testId="sec-faq">
          {ev.faqs.map((f, i) => (
            <div key={i} className="border border-slate-200 rounded-2xl p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-stone-700">FAQ #{i + 1}</div>
                <button type="button" onClick={() => delFaq(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </div>
              <input className={inputCls + " mb-2"} placeholder="Domanda" value={f.question || ""} onChange={(e) => updFaq(i, "question", e.target.value)} />
              <textarea className={inputCls} rows={2} placeholder="Risposta" value={f.answer || ""} onChange={(e) => updFaq(i, "answer", e.target.value)} />
            </div>
          ))}
          <button type="button" onClick={addFaq} className="inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline"><Plus className="w-4 h-4" /> Aggiungi FAQ</button>
        </Section>

        {/* 10. Download */}
        <Section title="Download / Allegati" kicker="Sezione 10" defaultOpen={false} testId="sec-downloads">
          {ev.downloads.map((d, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-2 items-start mb-2">
              <input className={inputCls} placeholder="Titolo" value={d.title || ""} onChange={(e) => updDownload(i, "title", e.target.value)} />
              <input className={inputCls} placeholder="URL file" value={d.url || ""} onChange={(e) => updDownload(i, "url", e.target.value)} />
              <input className={inputCls} placeholder="MIME (es. application/pdf)" value={d.mime_type || ""} onChange={(e) => updDownload(i, "mime_type", e.target.value)} />
              <button type="button" onClick={() => delDownload(i)} className="text-red-500 hover:text-red-700 mt-2.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addDownload} className="inline-flex items-center gap-2 text-sm text-[#12964A] hover:underline"><Plus className="w-4 h-4" /> Aggiungi allegato</button>
        </Section>

        {/* 11. Info utili */}
        <Section title="Informazioni utili (amenities)" kicker="Sezione 11" defaultOpen={false} testId="sec-amenities">
          <ChipToggle options={AMENITY_OPTIONS} labels={AMENITY_LABELS} value={ev.amenities || []} onChange={(v) => set("amenities", v)} />
        </Section>

        {/* 12. Link ufficiali evento */}
        <Section title="Link ufficiali evento" kicker="Sezione 12" defaultOpen={false} testId="sec-links">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Sito web evento"><input className={inputCls} value={ev.event_website || ""} onChange={(e) => set("event_website", e.target.value)} /></Field>
            <Field label="Facebook evento"><input className={inputCls} value={ev.event_facebook || ""} onChange={(e) => set("event_facebook", e.target.value)} /></Field>
            <Field label="Instagram evento"><input className={inputCls} value={ev.event_instagram || ""} onChange={(e) => set("event_instagram", e.target.value)} /></Field>
            <Field label="YouTube"><input className={inputCls} value={ev.event_youtube || ""} onChange={(e) => set("event_youtube", e.target.value)} /></Field>
          </div>
        </Section>

        {/* 13. SEO */}
        <Section title="SEO" kicker="Sezione 13" defaultOpen={false} testId="sec-seo">
          <div className="grid grid-cols-1 gap-3">
            <Field label="Meta title (opz.)"><input className={inputCls} value={ev.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} /></Field>
            <Field label="Meta description (opz.)"><textarea className={inputCls} rows={2} value={ev.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} /></Field>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Sitemap priority"><input type="number" step="0.1" min="0" max="1" className={inputCls} value={ev.sitemap_priority ?? 0.5} onChange={(e) => set("sitemap_priority", parseFloat(e.target.value))} /></Field>
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={!!ev.noindex} onChange={(e) => set("noindex", e.target.checked)} /> noindex</label>
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={!!ev.nofollow} onChange={(e) => set("nofollow", e.target.checked)} /> nofollow</label>
            </div>
          </div>
        </Section>

        {/* 14. Pubblicazione */}
        <Section title="Pubblicazione" kicker="Sezione 14" testId="sec-publish">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Stato">
              <select className={inputCls} value={ev.status} onChange={(e) => set("status", e.target.value)}>
                <option value="draft">Bozza</option><option value="published">Pubblicato</option>
                <option value="archived">Archiviato</option><option value="cancelled">Annullato</option>
                <option value="postponed">Rinviato</option>
              </select>
            </Field>
            <Field label="Priorità">
              <select className={inputCls} value={ev.priority} onChange={(e) => set("priority", e.target.value)}>
                <option value="normal">Normale</option><option value="featured">In evidenza</option><option value="main">Evento principale</option>
              </select>
            </Field>
            <Field label="Visibile dal (opz.)"><input type="datetime-local" className={inputCls} value={dtLocal(ev.visible_from)} onChange={(e) => set("visible_from", e.target.value ? new Date(e.target.value).toISOString() : null)} /></Field>
            <Field label="Visibile fino al (opz.)"><input type="datetime-local" className={inputCls} value={dtLocal(ev.visible_until)} onChange={(e) => set("visible_until", e.target.value ? new Date(e.target.value).toISOString() : null)} /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!ev.hide_after_end} onChange={(e) => set("hide_after_end", e.target.checked)} /> Nascondi automaticamente dopo la fine</label>
          </div>
        </Section>

        {/* 15. Interno */}
        <Section title="Note interne (solo admin)" kicker="Sezione 15" defaultOpen={false} testId="sec-internal">
          <Field label="Note interne"><textarea className={inputCls} rows={3} value={ev.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} /></Field>
          <div className="mt-3">
            <Field label="Motivo ultima modifica (opz., visibile solo admin nello storico)"><input className={inputCls} value={ev.last_change_reason || ""} onChange={(e) => set("last_change_reason", e.target.value)} data-testid="input-change-reason" /></Field>
          </div>
        </Section>

        {/* 16. Storico modifiche */}
        {!isNew && ev.history && ev.history.length > 0 && (
          <Section title="Storico modifiche" kicker="Audit log" defaultOpen={false} testId="sec-history">
            <ul className="text-sm">
              {ev.history.map((h) => (
                <li key={h.id} className="py-2 border-b border-slate-100 flex items-start gap-3">
                  <div className="text-[11px] font-mono text-slate-500 w-40 flex-shrink-0">{new Date(h.created_at).toLocaleString("it-IT")}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-stone-800">{h.operation} · v{h.version_after || "?"}</div>
                    <div className="text-xs text-slate-500">{h.user_email || h.user_id || "—"}</div>
                    {h.reason && <div className="text-xs text-slate-600 mt-1 italic">Motivo: {h.reason}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
};

export default EventForm;
